import UserAppSettingsApi from 'flow/api/userappsettings';
import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQuery } from 'flow/hooks/api/useAuthenticatedQuery';
import { setCallMethod, setMakeCallsEnabled } from 'flow/actions/app-actions';
import { setSoftphoneShouldBeRegistered } from 'flow/actions/phone-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectIsLoggedIn,
    selectMakeCallsEnabled,
    selectTemporaryPasswordState,
} from 'flow/selectors/app-selectors';
import { CallMethodType } from 'flow/utils/call-utils';
import { isSoftphoneExplicitlyDisabled } from 'flow/utils/browser-utils';

const INCOMING_CALLS_SETTING = 'incoming_calls';
const OUTGOING_CALLS_SETTING = 'outgoing_calls';

export enum UserAppSettingOption {
    ON = 'on',
    OFF = 'off',
    ALWAYS_ASK = 'always_ask',
    PER_CLIENT = 'per_client',
    CALL_DIRECT_VOIP = 'call_direct_voip',
    CALL_WITH_MOBILE = 'call_with_mobile',
    CALL_WITH_OTHER_IP_PHONE = 'call_with_other_ip_phone',
    UNKNOWN = 'unknown',
}

interface UserAppSettingDTO {
    key: string;
    value: UserAppSettingOption;
}

export const USER_APP_SETTINGS_QUERY_KEY = ['userAppSettings'];
const queryKey = USER_APP_SETTINGS_QUERY_KEY;

const fetchSetting = (settingName: string): Promise<UserAppSettingDTO> =>
    new Promise((resolve, reject) => {
        UserAppSettingsApi.getSetting(
            { onResult: resolve, onError: reject, onClientError: reject },
            settingName,
        );
    });

const updateSetting = (dto: UserAppSettingDTO): Promise<UserAppSettingDTO> =>
    new Promise((resolve, reject) => {
        UserAppSettingsApi.updateSetting(
            { onResult: resolve, onError: reject, onClientError: reject },
            dto,
        );
    });

const useCallSetting = (settingName: string, enabled = true) =>
    useAuthenticatedQuery({
        queryKey: [...queryKey, settingName],
        queryFn: () => fetchSetting(settingName),
        enabled,
        staleTime: 30 * 60 * 1000,
    });

const useUpdateCallSetting = (settingName: string) => {
    const queryClient = useQueryClient();
    const settingQueryKey = [...queryKey, settingName];

    return useMutation({
        mutationFn: (value: UserAppSettingOption) =>
            updateSetting({ key: settingName, value }),
        onMutate: async (value: UserAppSettingOption) => {
            await queryClient.cancelQueries({ queryKey: settingQueryKey });
            const previous =
                queryClient.getQueryData<UserAppSettingDTO>(settingQueryKey);
            queryClient.setQueryData<UserAppSettingDTO>(settingQueryKey, {
                key: settingName,
                value,
            });
            return { previous };
        },
        onError: (_err, _value, context) => {
            queryClient.setQueryData(settingQueryKey, context?.previous);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: settingQueryKey });
        },
    });
};

export const useIncomingCallSettings = () =>
    useCallSetting(INCOMING_CALLS_SETTING);

export const useUpdateIncomingCallSetting = () =>
    useUpdateCallSetting(INCOMING_CALLS_SETTING);

export const useSyncIncomingCallSetting = () => {
    const dispatch = useAppDispatch();
    const { data: incomingSettings } = useIncomingCallSettings();
    const settingOption = incomingSettings?.value;
    const loggedInWithTempPass = useAppSelector(selectTemporaryPasswordState);

    useEffect(() => {
        if (loggedInWithTempPass) {
            return;
        }
        if (
            settingOption === UserAppSettingOption.ON ||
            settingOption === UserAppSettingOption.OFF
        ) {
            dispatch(
                setSoftphoneShouldBeRegistered(
                    settingOption === UserAppSettingOption.ON,
                ),
            );
        }
    }, [dispatch, loggedInWithTempPass, settingOption]);
};

export const useOutgoingCallSettings = () =>
    useCallSetting(OUTGOING_CALLS_SETTING);

export const useUpdateOutgoingCallSetting = () =>
    useUpdateCallSetting(OUTGOING_CALLS_SETTING);

const SETTING_TO_CALL_METHOD = {
    [UserAppSettingOption.ALWAYS_ASK]: CallMethodType.ALWAYS_ASK,
    [UserAppSettingOption.CALL_WITH_MOBILE]: CallMethodType.MOBILE,
    [UserAppSettingOption.CALL_WITH_OTHER_IP_PHONE]: CallMethodType.IP_PHONE,
    [UserAppSettingOption.CALL_DIRECT_VOIP]: CallMethodType.LOCAL,
} as const;

export const getCallMethodFromSetting = (
    option: UserAppSettingOption | undefined,
) =>
    option !== undefined
        ? (SETTING_TO_CALL_METHOD[
              option as keyof typeof SETTING_TO_CALL_METHOD
          ] ?? null)
        : null;

export const getSettingFromCallMethod = (
    method: unknown,
): UserAppSettingOption => {
    if (method === CallMethodType.ALWAYS_ASK)
        return UserAppSettingOption.ALWAYS_ASK;
    if (method === CallMethodType.MOBILE)
        return UserAppSettingOption.CALL_WITH_MOBILE;
    if (method === CallMethodType.IP_PHONE)
        return UserAppSettingOption.CALL_WITH_OTHER_IP_PHONE;
    // The server-side setting has no companion value, and relaying to a desktop install is
    // inherently per-surface - "use this setting everywhere" cannot represent it. Falling through
    // to CALL_DIRECT_VOIP would persist a method the client is not actually using.
    if (method === CallMethodType.COMPANION)
        return UserAppSettingOption.PER_CLIENT;
    return UserAppSettingOption.CALL_DIRECT_VOIP;
};

export const isSettingAppliedEverywhere = (
    option: UserAppSettingOption | undefined,
): boolean =>
    option !== undefined &&
    option !== UserAppSettingOption.PER_CLIENT &&
    option !== UserAppSettingOption.UNKNOWN;

export const useSyncOutgoingCallSetting = () => {
    const dispatch = useAppDispatch();
    const { data: outgoingSettings } = useOutgoingCallSettings();
    const settingOption = outgoingSettings?.value;

    useEffect(() => {
        // A setting that applies everywhere overrides this client's own "Make calls" choice.
        if (isSettingAppliedEverywhere(settingOption)) {
            dispatch(
                setMakeCallsEnabled(settingOption !== UserAppSettingOption.OFF),
            );
        }

        const callMethod = getCallMethodFromSetting(settingOption);
        if (callMethod) {
            dispatch(setSoftphoneShouldBeRegistered(true));
            dispatch(setCallMethod(callMethod));
        } else if (
            settingOption === UserAppSettingOption.PER_CLIENT &&
            !isSoftphoneExplicitlyDisabled()
        ) {
            dispatch(setSoftphoneShouldBeRegistered(true));
        }
    }, [dispatch, settingOption]);
};

// Not derived from the softphone registration, which belongs to incoming calls (AC-1902).
export const useOutgoingCallsEnabled = (): boolean =>
    useAppSelector(selectMakeCallsEnabled);

export const useSyncCallSettings = () => {
    const queryClient = useQueryClient();
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    useSyncIncomingCallSetting();
    useSyncOutgoingCallSetting();

    useEffect(() => {
        if (!isLoggedIn) {
            queryClient.removeQueries({
                queryKey: USER_APP_SETTINGS_QUERY_KEY,
            });
        }
    }, [isLoggedIn, queryClient]);
};
